import { z } from "zod";
import { defineStepConfig } from "../../../registry/helpers";
import { entityOfType } from "../../../utils/system-entities";
import { SystemEntity } from "@erp/shared-types";

/**
 * Create Sales Order Draft Step
 *
 * Creates staged customer purchase orders (sales order drafts) from an inbound email.
 * Processes each document classified as a purchase order independently, creating a separate
 * staged PO for each document.
 *
 * This step:
 * - Filters documents to only those classified as PURCHASE_ORDER
 * - For each PO document:
 *   - Extracts purchase order data from that specific document using OCR results
 *   - Identifies the customer using embeddings or fallback methods
 *   - Matches line items to customer products
 *   - Creates StagedCustomerPurchaseOrder record with processing errors if customer not found
 * - Records memory bank note usages for each staged PO
 * - Returns array of all created staged purchase orders
 *
 * Note: This is a backend-only step that requires database access.
 * The handler is registered in backend/src/workflows/steps/order-management/
 */
export const createSalesOrderDraftStep = defineStepConfig({
  type: "create_sales_order_draft",
  name: "Create Sales Order Draft",
  description:
    "Extract purchase order data from classified documents and create sales order drafts",
  category: "Data Access",

  inputSchema: z.object({
    inboundEmail: entityOfType(SystemEntity.INBOUND_EMAIL).describe(
      "The inbound email containing purchase order documents",
    ),
    // notes: z.array(z.string()).describe(
    //   "Notes to provide context to the LLM for better extraction and refinement"
    // ),
  }),

  outputSchema: z.object({
    // No outputs needed - staged POs are created in the database
  }),

  configSchema: z.object({
    skipClassificationCheck: z
      .boolean()
      .optional()
      .describe(
        "Skip document classification checks (used for legacy processing paths)",
      ),
    customerId: z
      .uuid()
      .optional()
      .describe(
        "Pre-identified customer ID to use instead of running customer matching",
      ),
    // Future: add config for matching thresholds, auto-approval rules
  }),

  emits: [] as const,

  execution: {
    startToCloseTimeout: "5 minutes",
    retry: {
      maximumAttempts: 1,
    },
    nonRetryableErrorTypes: ["ValidationError", "SchemaValidationError"],
  },
});
